import {
  ALLOWED_TOOL_NAMES,
  processNonReportAgentChunks,
} from "@/src/utils/groupByAgent";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ThinkingReasoningList, { ReasoningItem } from "./ThinkingReasoningList";

import ThinkingHeader from "./ThinkingHeader";
import { useAgentMessage } from "@/src/hooks/useAgentMessage";

export interface ThinkingProps {
  agents: any;
  chatId?: string;
  messageId?: string;
  isShowAnswer?: boolean;
  createdAt?: string;
  agentTypes?: string[];
  isLast?: boolean;
  loading?: boolean;
  isTab?: boolean;
  noAnimate?: boolean;
  enabled?: boolean;
}

const Thinking: React.FC<ThinkingProps> = (props) => {
  const {
    agents,
    agentTypes = [],
    chatId = "",
    messageId = "",
    isShowAnswer,
    isLast,
    loading,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);

  const enabled =
    isExpanded && (!isLast || (!!chatId && !!messageId && !loading));

  const { data: agentMessage } = useAgentMessage({
    chatId,
    messageId,
    enabled,
  });

  const formatData = useMemo(() => {
    return processNonReportAgentChunks(agentMessage || [], {
      allowedToolNames: [...ALLOWED_TOOL_NAMES],
    });
  }, [agentMessage]);

  const reasoningItems: ReasoningItem[] = useMemo(() => {
    const sourceData = enabled ? formatData : agents;
    if (!sourceData) {
      return [];
    }

    const items: ReasoningItem[] = [];
    const allAgentKeys = Object.keys(sourceData);

    allAgentKeys.forEach((agentKey) => {
      const agentData = sourceData[agentKey];
      if (!Array.isArray(agentData)) {
        return;
      }

      agentData.forEach((item: any) => {
        if (item.type === "reasoning" && item.data?.text) {
          items.push({
            id: item.data.id || `reasoning-${items.length}`,
            text: item.data.text,
          });
        }
      });
    });

    return items;
  }, [enabled, formatData, agents]);

  const latestReasoningText = useMemo(() => {
    if (reasoningItems.length === 0) {
      return "";
    }
    const last = reasoningItems[reasoningItems.length - 1];
    const text = last.text || "";
    return text.length > 80 ? text.slice(0, 80) + "..." : text;
  }, [reasoningItems]);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    if (loading && isLast && !isShowAnswer) {
      setIsExpanded(false);
    }
  }, [loading, isLast, isShowAnswer]);

  const shouldShow = agentTypes?.length > 0 || (loading && isLast);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="flex flex-col pb-5">
      <ThinkingHeader
        data-openreplay-label="Thinking · Thinking · ThinkingHeader"
        isShowAnswer={!!isShowAnswer}
        agentKeys={agentTypes}
        onClick={handleToggle}
        chatId={chatId}
        loading={loading}
        isLast={isLast}
        isExpanded={isExpanded}
        latestReasoningText={
          loading && isLast && !isShowAnswer ? latestReasoningText : undefined
        }
      />
      <ThinkingReasoningList
        items={reasoningItems}
        isExpanded={isExpanded}
        isLoading={loading && isLast}
      />
    </div>
  );
};

export default Thinking;
